import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Lock, Mail, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";
import { countryOptions } from "@/countries";

const inputCls = "w-full rounded-md border border-[#1F293D] bg-[#0B0E14] px-3 py-2.5 text-sm text-slate-200 focus:border-[#00FF87] focus:outline-none";
const sectionTitle = "mb-2 font-mono2 text-[10px] uppercase tracking-widest text-[#00F0FF]";
const smallBtn = "rounded-md border border-[#1F293D] px-4 py-2 font-mono2 text-[11px] font-bold uppercase tracking-wider text-slate-200 hover:border-[#00FF87]/60 hover:text-[#00FF87] disabled:opacity-40";
const rowCls = "flex items-center justify-between border-b border-[#1F293D] py-3";

function PreferencesSection({ me, reload }) {
  const { t, lang, setLang } = useI18n();
  const [nl, setNl] = useState(null);
  const premium = me.premium_access;

  const loadNl = useCallback(() => api.get("/account/newsletter").then((r) => setNl(r.data)).catch(() => {}), []);
  useEffect(() => { loadNl(); }, [loadNl]);

  const setPref = async (patch) => {
    try {
      await api.patch("/account/preferences", patch);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setNewsletter = async (subscribe) => {
    try {
      await api.post("/account/newsletter", { subscribe, lang });
      toast.success(subscribe ? t("account.newsletter_done") : t("account.newsletter_off_done"));
      loadNl();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggle = (checked, onChange, testid, disabled) => (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled} data-testid={testid} onClick={() => onChange(!checked)}
      className={`relative h-5 w-9 shrink-0 border transition-colors disabled:opacity-40 ${checked ? "border-[#00FF87] bg-[#00FF87]" : "border-[#1F293D] bg-[#0B0E14]"}`}>
      <span className={`absolute top-0.5 h-3.5 w-3.5 transition-[left] ${checked ? "left-[18px] bg-[#0B0E14]" : "left-0.5 bg-slate-500"}`} />
    </button>
  );

  const status = nl?.status || "none";
  return (
    <>
      <section className="mb-10 rounded-xl border border-[#1F293D] p-5" data-testid="account-preferences">
        <h2 className={sectionTitle}>{t("account.prefs_title")}</h2>
        <div className={rowCls}>
          <span className="text-xs text-slate-400">{t("account.lang_label")}</span>
          <div className="flex overflow-hidden border border-[#1F293D]">
            {["fr", "en"].map((l) => (
              <button key={l} onClick={() => { setLang(l); setPref({ lang: l }); }} data-testid={`account-lang-${l}`} className={`px-3 py-1 font-mono2 text-[10px] font-bold uppercase ${lang === l ? "bg-[#00FF87] text-[#07090E]" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className={rowCls}>
          <span className="text-xs text-slate-400">{t("account.alerts_enabled")}</span>
          {premium ? toggle(me.alerts_enabled !== false, (v) => setPref({ alerts_enabled: v }), "pref-alerts-toggle")
            : <Link to="/pricing" data-testid="alerts-locked-cta" className="flex items-center gap-1.5 font-mono2 text-[9px] uppercase tracking-wider text-slate-500 hover:text-[#00FF87]"><Lock className="h-3 w-3" /> {t("account.premium_only")}</Link>}
        </div>
        <div className={`${rowCls} border-b-0`}>
          <span className="text-xs text-slate-400">{t("account.alert_unconfirmed")}</span>
          {premium ? toggle(!!me.alert_unconfirmed, (v) => setPref({ alert_unconfirmed: v }), "pref-unconfirmed-toggle")
            : <span className="flex items-center gap-1.5 font-mono2 text-[9px] uppercase tracking-wider text-slate-500"><Lock className="h-3 w-3" /> {t("account.premium_only")}</span>}
        </div>
      </section>

      <section className="mb-10 rounded-xl border border-[#1F293D] p-5" data-testid="account-newsletter">
        <h2 className={sectionTitle}>{t("account.newsletter_title")}</h2>
        {status === "pending" ? (
          <div data-testid="newsletter-pending">
            <p className="text-xs text-[#FFD600]">{t("account.newsletter_pending")}</p>
            <button onClick={() => setNewsletter(true)} data-testid="newsletter-resend-button" className="mt-2 font-mono2 text-[10px] uppercase tracking-wider text-[#00F0FF] hover:underline">{t("account.newsletter_resend")}</button>
          </div>
        ) : status === "confirmed" ? (
          <div data-testid="newsletter-subscribed">
            <div className={rowCls}>
              <span className="text-xs text-slate-200" data-testid="newsletter-state">{t("account.newsletter_on")}</span>
              {toggle(true, () => setNewsletter(false), "newsletter-toggle")}
            </div>
            {nl?.consent_at && <p className="mt-2 font-mono2 text-[10px] text-slate-500" data-testid="newsletter-consent-date">{t("account.newsletter_since", { date: new Date(nl.consent_at).toLocaleDateString() })}</p>}
            <p className="mt-1 text-[11px] text-slate-500">{t("account.newsletter_note")}</p>
          </div>
        ) : (
          <div data-testid="newsletter-off">
            <div className={rowCls}>
              <span className="text-xs text-slate-200" data-testid="newsletter-state">{t("account.newsletter_off")}</span>
              {toggle(false, () => setNewsletter(true), "newsletter-toggle", !nl)}
            </div>
            <p className="mt-2 text-[11px] text-slate-500">{t("account.newsletter_opt_in")}</p>
          </div>
        )}
      </section>
    </>
  );
}

export default function AccountPage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [country, setCountry] = useState("FR");
  const [busyProfile, setBusyProfile] = useState(false);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [busyPw, setBusyPw] = useState(false);

  const [newEmail, setNewEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [busyEmail, setBusyEmail] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/account");
      setMe(r.data);
      setFirstName(r.data.first_name || "");
      setLastName(r.data.last_name || "");
      setCountry(r.data.country || "FR");
    } catch {
      navigate("/auth");
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const saveProfile = async () => {
    setBusyProfile(true);
    try {
      const r = await api.patch("/account/profile", { first_name: firstName, last_name: lastName, country });
      toast.success(t("account.profile_saved"));
      if (r.data.vat_review_flagged) toast.info(t("account.vat_flagged"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyProfile(false);
    }
  };

  const changePassword = async () => {
    if (newPw !== newPw2) return toast.error(t("account.password_mismatch"));
    setBusyPw(true);
    try {
      await api.post("/account/password", { current_password: curPw, new_password: newPw, confirm_password: newPw2 });
      toast.success(t("account.password_done"));
      setCurPw(""); setNewPw(""); setNewPw2("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyPw(false);
    }
  };

  const requestEmail = async () => {
    setBusyEmail(true);
    try {
      await api.post("/account/email/request", { new_email: newEmail });
      toast.success(t("account.email_code_sent"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyEmail(false);
    }
  };

  const confirmEmail = async () => {
    setBusyEmail(true);
    try {
      await api.post("/account/email/confirm", { code: emailCode });
      toast.success(t("account.email_changed"));
      setEmailCode(""); setNewEmail("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyEmail(false);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.post("/account/delete", { password, confirm });
      toast.success(t("account.delete_done"));
      navigate("/");
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!me) return <p className="pt-16 text-center font-mono2 text-sm text-slate-500">{t("common.loading")}</p>;

  return (
    <div className="mx-auto max-w-xl pt-10" data-testid="account-page">
      <h1 className="mb-6 font-display text-3xl font-black uppercase tracking-tight text-slate-50">{t("account.title")}</h1>

      <section className="mb-10" data-testid="account-profile">
        <h2 className={sectionTitle}>{t("account.profile")}</h2>
        <div className={rowCls}><span className="text-xs text-slate-500">{t("admin.email")}</span><span className="font-mono2 text-xs text-slate-200" data-testid="account-email">{me.email}</span></div>
        <div className={rowCls}><span className="text-xs text-slate-500">{t("account.plan")}</span><span className="font-mono2 text-xs uppercase text-[#FFD600]">{me.plan || "free"}</span></div>
        <div className={rowCls}><span className="text-xs text-slate-500">{t("account.member_since")}</span><span className="font-mono2 text-xs text-slate-400">{me.created_at ? new Date(me.created_at).toLocaleDateString() : "—"}</span></div>
        <div className={rowCls}><span className="text-xs text-slate-500">{t("account.last_login")}</span><span className="font-mono2 text-xs text-slate-400">{me.last_login ? new Date(me.last_login).toLocaleString() : "—"}</span></div>
      </section>

      <section className="mb-10 rounded-xl border border-[#1F293D] p-5" data-testid="account-profile-edit">
        <h2 className={sectionTitle}>{t("account.edit_profile")}</h2>
        <p className="mb-4 text-[11px] text-slate-500">{t("account.profile_note")}</p>
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="profile-first-name" placeholder={t("auth.first_name")} className={inputCls} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <input data-testid="profile-last-name" placeholder={t("auth.last_name")} className={inputCls} value={lastName} onChange={(e) => setLastName(e.target.value)} />
          <select data-testid="profile-country" className={`${inputCls} col-span-2`} value={country} onChange={(e) => setCountry(e.target.value)}>
            {countryOptions(lang).map((c) => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
          </select>
        </div>
        <button data-testid="profile-save-button" onClick={saveProfile} disabled={busyProfile || !firstName.trim() || !lastName.trim()} className={`${smallBtn} mt-4`}>
          {busyProfile ? t("common.loading") : t("account.save")}
        </button>
      </section>

      <section className="mb-10 rounded-xl border border-[#1F293D] p-5" data-testid="account-password">
        <h2 className={sectionTitle}><KeyRound className="mr-1 inline h-3 w-3" /> {t("account.password_title")}</h2>
        <div className="space-y-3">
          <input type="password" data-testid="current-password-input" placeholder={t("account.current_password")} className={inputCls} value={curPw} onChange={(e) => setCurPw(e.target.value)} />
          <input type="password" data-testid="new-password-input" placeholder={t("account.new_password")} className={inputCls} value={newPw} onChange={(e) => setNewPw(e.target.value)} />
          <input type="password" data-testid="confirm-password-input" placeholder={t("account.confirm_password")} className={inputCls} value={newPw2} onChange={(e) => setNewPw2(e.target.value)} />
        </div>
        <button data-testid="change-password-button" onClick={changePassword} disabled={busyPw || !curPw || newPw.length < 8 || !newPw2} className={`${smallBtn} mt-4`}>
          {busyPw ? t("common.loading") : t("account.password_btn")}
        </button>
      </section>

      <section className="mb-10 rounded-xl border border-[#1F293D] p-5" data-testid="account-email-change">
        <h2 className={sectionTitle}><Mail className="mr-1 inline h-3 w-3" /> {t("account.email_title")}</h2>
        <div className={rowCls}><span className="text-xs text-slate-500">{t("account.email_current")}</span><span className="font-mono2 text-xs text-slate-200">{me.email}</span></div>
        {me.pending_email ? (
          <div className="mt-4 space-y-3">
            <p className="text-[11px] text-[#FFD600]" data-testid="email-pending-note">{t("account.email_pending", { email: me.pending_email })}</p>
            <input data-testid="email-code-input" inputMode="numeric" maxLength={6} placeholder={t("account.email_code")} className={inputCls} value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))} />
            <button data-testid="email-confirm-button" onClick={confirmEmail} disabled={busyEmail || emailCode.length !== 6} className={smallBtn}>
              {busyEmail ? t("common.loading") : t("account.email_confirm_btn")}
            </button>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <input type="email" data-testid="new-email-input" placeholder={t("account.email_new")} className={inputCls} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            <button data-testid="email-request-button" onClick={requestEmail} disabled={busyEmail || !newEmail.includes("@")} className={smallBtn}>
              {busyEmail ? t("common.loading") : t("account.email_request_btn")}
            </button>
          </div>
        )}
      </section>

      <PreferencesSection me={me} reload={load} />

      <section className="rounded-xl border border-red-500/30 p-5" data-testid="account-danger-zone">
        <h2 className="mb-2 flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-widest text-red-400">
          <ShieldAlert className="h-3.5 w-3.5" /> {t("account.danger_zone")}
        </h2>
        <p className="mb-4 text-xs leading-relaxed text-slate-400">{t("account.delete_text")}</p>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t("account.delete_password")}
          data-testid="delete-password-input"
          className={`${inputCls} mb-3 focus:border-red-500`}
        />
        <label className="mb-4 flex items-start gap-2 text-[11px] text-slate-400">
          <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="delete-confirm-checkbox" className="mt-0.5 h-3.5 w-3.5 accent-red-500" />
          {t("account.delete_confirm")}
        </label>
        <button
          onClick={doDelete}
          disabled={busy || !confirm || !password}
          data-testid="delete-account-button"
          className="rounded-md border border-red-500/50 px-4 py-2 font-mono2 text-[11px] font-bold uppercase tracking-wider text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-40"
        >
          {busy ? t("common.loading") : t("account.delete_btn")}
        </button>
      </section>
    </div>
  );
}
